import DOMAIN from 'assets/domains.json';
import { DomainKey } from 'data/constants/domainApiKey';
import {
    getDomainPalette,
    getDomainPaletteCssVars,
    resolveThemeAlias,
    type DomainPalette,
} from 'utils/domainPalette';

/**
 * Shared domain branding — single source of truth for logos used across
 * Login, Header, VerificationCode, AppLoader, and other surfaces.
 *
 * Sources consolidated from:
 * - src/views/v2/auth/containers/login.tsx
 * - src/components/Header/Header.tsx
 */

export type DomainLogoVariant = 'full' | 'header' | 'minimal' | 'compact';

export interface DomainBranding {
    /** Domain key (e.g. MEDIACRUSH, LOCAL) */
    key: string;
    /** Full / marketing logo (login, verification, loaders on light bg) */
    logo: string;
    /** Header desktop logo (falls back to `logo` when omitted in the map) */
    logoHeader: string;
    /** Compact header / mobile icon */
    logoMinimal: string;
    /** Browser tab favicon (falls back to `logoMinimal`) */
    favicon: string;
    /** Preferred width on auth / marketing pages */
    width: string;
    /** Preferred width for compact layouts (AppLoader, etc.) */
    compactWidth: string;
    /** Show Aliro tagline ("Your gateway to the world") */
    showTagline: boolean;
    /** Optional external marketing site (default Aliro brand) */
    externalUrl?: string;
    /** Accent palette for themed surfaces (profile editor, etc.) */
    palette: DomainPalette;
}

type DomainBrandingOverride = Partial<
    Omit<DomainBranding, 'key' | 'logoHeader' | 'favicon' | 'palette'> & {
        logoHeader?: string;
        favicon?: string;
    }
>;

export const PREVIEW_THEME_STORAGE_KEY = 'aliro.previewTheme';

const PREVIEW_HOST_KEYS = new Set(['LOCAL', 'DEMO', 'SANDBOX', 'FEATURE', 'DEV']);

const ALIRO_LOGO = '/Aliro logo SVG blue classic 1.svg';
const ALIRO_LOGO_MINIMAL = '/aliro_icon_32_2.png';
const ALIRO_EXTERNAL_URL = 'https://aliroimmigration.com';
const DEFAULT_TAGLINE = 'Your gateway to the world';

const DEFAULT_BRANDING: Omit<DomainBranding, 'key' | 'palette'> = {
    logo: ALIRO_LOGO,
    logoHeader: ALIRO_LOGO,
    logoMinimal: ALIRO_LOGO_MINIMAL,
    favicon: ALIRO_LOGO_MINIMAL,
    width: '280px',
    compactWidth: '180px',
    showTagline: true,
    externalUrl: ALIRO_EXTERNAL_URL,
};

/**
 * Per-domain brand config.
 * - `logo`        → Login / Verification / AppLoader
 * - `logoHeader`  → Header desktop (defaults to `logo`)
 * - `logoMinimal` → Header mobile / compact icon
 */
const DOMAIN_BRANDING_MAP: Record<string, DomainBrandingOverride> = {
    [DOMAIN.MEDIACRUSH]: {
        logo: '/uiscanadalogo.png',
        logoMinimal: '/uis-logo-small-white.png',
        width: '320px',
        compactWidth: '200px',
        showTagline: false,
        externalUrl: undefined,
    },
    [DOMAIN.UISAUSTRALIA]: {
        logo: '/uisAusLogo.png',
        logoMinimal: '/uisAus-icon.png',
        width: '320px',
        compactWidth: '200px',
        showTagline: false,
        externalUrl: undefined,
    },
    [DOMAIN.UIS_AUSTRALIA]: {
        logo: '/uisAusLogo.png',
        logoMinimal: '/uisAus-icon.png',
        width: '320px',
        compactWidth: '200px',
        showTagline: false,
        externalUrl: undefined,
    },
    [DOMAIN.LOOKNOFURTHER]: {
        // Auth pages historically used inverted; Header desktop uses large.
        logo: '/lookNoFurtherInverted.svg',
        logoHeader: '/LookNoFurtherLarge.svg',
        logoMinimal: '/lookNoFurtherInverted.svg',
        width: '330px',
        compactWidth: '200px',
        showTagline: false,
        externalUrl: undefined,
    },
    [DOMAIN.TEAMSTAFFINGINTERNATIONAL]: {
        logo: '/tsi-logo.png',
        logoMinimal: '/tsi-icon.png',
        width: '450px',
        compactWidth: '240px',
        showTagline: false,
        externalUrl: undefined,
    },
    [DOMAIN.MDCPORTAL]: {
        logo: '/mdc_Logo_colour_tagline.png',
        logoMinimal: '/mdc_Icon_colour.png',
        width: '330px',
        compactWidth: '200px',
        showTagline: false,
        externalUrl: undefined,
    },
    [DOMAIN.RESOLVED]: {
        logo: '/Resolved logo.png',
        logoMinimal: '/Resolved logo minimal.png',
        width: '330px',
        compactWidth: '200px',
        showTagline: false,
        externalUrl: undefined,
    },
    [DOMAIN.BASTIN]: {
        logo: '/bastin-full.png',
        logoMinimal: '/bastin-small.png',
        width: '370px',
        compactWidth: '220px',
        showTagline: false,
        externalUrl: undefined,
    },
    [DOMAIN.GLOBALCITIZENS]: {
        logo: '/globalCitizens.svg',
        logoMinimal: '/globalCitizens.svg',
        width: '330px',
        compactWidth: '200px',
        showTagline: false,
        externalUrl: undefined,
    },
    [DOMAIN.GREATNORTHVISA]: {
        logo: '/GNV-light bg.svg',
        logoMinimal: '/GNV-light bg.svg',
        width: '330px',
        compactWidth: '200px',
        showTagline: false,
        externalUrl: undefined,
    },
    [DOMAIN.JOSHUASLAYEN]: {
        // Auth: full dark logo; Header: compact icon
        logo: '/joshua-slayen-dark.png',
        logoHeader: '/joshua-icon.png',
        logoMinimal: '/joshua-icon.png',
        width: '400px',
        compactWidth: '220px',
        showTagline: false,
        externalUrl: undefined,
    },
    [DOMAIN.UISUK]: {
        logo: '/uisUK.svg',
        logoMinimal: '/uisUKmini.png',
        width: '330px',
        compactWidth: '200px',
        showTagline: false,
        externalUrl: undefined,
    },
    [DOMAIN.BIZBUDDY]: {
        logo: '/BizBuddyLogo-Blue-Transparent.png',
        logoMinimal: '/BizBuddyMinimal.png',
        width: '330px',
        compactWidth: '200px',
        showTagline: false,
        externalUrl: undefined,
    },
    [DOMAIN.NEWDAYIMMIGRATION]: {
        logo: '/newDayImmigrationLogo.png',
        logoMinimal: '/newDayImmigrationLogo.png',
        width: '330px',
        compactWidth: '200px',
        showTagline: false,
        externalUrl: undefined,
    },
    // Aliro logo without tagline (matches prior Login/Verification maps)
    [DOMAIN.FEATURE]: {
        logo: ALIRO_LOGO,
        logoMinimal: ALIRO_LOGO_MINIMAL,
        width: '330px',
        compactWidth: '180px',
        showTagline: false,
        externalUrl: undefined,
    },
    [DOMAIN.LOCAL]: {
        logo: ALIRO_LOGO,
        logoMinimal: ALIRO_LOGO_MINIMAL,
        width: '280px',
        compactWidth: '180px',
        showTagline: true,
        externalUrl: ALIRO_EXTERNAL_URL,
    },
    [DOMAIN.DEMO]: {
        logo: ALIRO_LOGO,
        logoMinimal: ALIRO_LOGO_MINIMAL,
        width: '280px',
        compactWidth: '180px',
        showTagline: true,
        externalUrl: ALIRO_EXTERNAL_URL,
    },
    [DOMAIN.ALIRO]: {
        logo: ALIRO_LOGO,
        logoMinimal: ALIRO_LOGO_MINIMAL,
        width: '280px',
        compactWidth: '180px',
        showTagline: true,
        externalUrl: ALIRO_EXTERNAL_URL,
    },
    [DOMAIN.ALIROAUSTRALIA]: {
        logo: '/aliro-australia.svg',
        logoHeader: '/aliro-australia.svg',
        logoMinimal: '/aliro-australia-favicon.svg',
        favicon: '/aliro-australia-favicon.svg',
        width: '280px',
        compactWidth: '180px',
        showTagline: true,
        externalUrl: ALIRO_EXTERNAL_URL,
    },
    [DOMAIN.ALIROCANADA]: {
        logo: '/aliro-canada.svg',
        logoHeader: '/aliro-canada.svg',
        logoMinimal: '/aliro-canada-favicon.svg',
        favicon: '/aliro-canada-favicon.svg',
        width: '280px',
        compactWidth: '180px',
        showTagline: true,
        externalUrl: ALIRO_EXTERNAL_URL,
    },
    [DOMAIN.ALIROUK]: {
        logo: '/aliro-uk.svg',
        logoHeader: '/aliro-uk.svg',
        logoMinimal: '/aliro-uk-favicon.svg',
        favicon: '/aliro-uk-favicon.svg',
        width: '280px',
        compactWidth: '180px',
        showTagline: true,
        externalUrl: ALIRO_EXTERNAL_URL,
    },
    [DOMAIN.AUSSIEMIGRATE]: {
        logo: '/aussie-migrate-large.png',
        logoHeader: '/aussie-migrate-large.png',
        logoMinimal: '/aussie-migrate-icon.png',
        favicon: '/aussie-migrate-favicon.png',
        width: '330px',
        compactWidth: '200px',
        showTagline: false,
        externalUrl: undefined,
    },
    [DOMAIN.SANDBOX]: {
        logo: '/aussie-migrate-large.png',
        logoHeader: '/aussie-migrate-long.png',
        logoMinimal: '/aussie-migrate-icon.png',
        favicon: '/aussie-migrate-favicon.png',
        width: '330px',
        compactWidth: '200px',
        showTagline: false,
        externalUrl: undefined,
    },
    [DOMAIN.EASY]: {
        logo: '/aussie-migrate-large.png',
        logoHeader: '/aussie-migrate-large.png',
        logoMinimal: '/aussie-migrate-icon.png',
        favicon: '/aussie-migrate-favicon.png',
        width: '330px',
        compactWidth: '200px',
        showTagline: false,
        externalUrl: undefined,
    },
};

function resolveKey(domainKey?: string): string {
    return domainKey || DomainKey || '';
}

function canPreviewThemes(hostKey: string): boolean {
    return PREVIEW_HOST_KEYS.has(hostKey);
}

/**
 * On preview hosts (localhost / demo / sandbox), `?theme=uis` (or a domain
 * key) swaps logos + palette so we can check tenant branding without a hosts
 * file. `?theme=default` clears a previously stored override.
 */
export function getPreviewThemeOverride(hostKey: string): string | undefined {
    if (!canPreviewThemes(hostKey) || typeof window === 'undefined') {
        return undefined;
    }

    const param = new URLSearchParams(window.location.search).get('theme');
    if (param !== null) {
        if (param === '' || param.toLowerCase() === 'default') {
            return undefined;
        }
        return resolveThemeAlias(param);
    }

    try {
        const stored = window.localStorage.getItem(PREVIEW_THEME_STORAGE_KEY);
        return stored ? resolveThemeAlias(stored) : undefined;
    } catch {
        return undefined;
    }
}

export function syncPreviewThemeFromQuery(): void {
    if (typeof window === 'undefined') {
        return;
    }
    const hostKey = resolveKey();
    if (!canPreviewThemes(hostKey)) {
        return;
    }

    const param = new URLSearchParams(window.location.search).get('theme');
    if (param === null) {
        return;
    }

    try {
        if (param === '' || param.toLowerCase() === 'default') {
            window.localStorage.removeItem(PREVIEW_THEME_STORAGE_KEY);
            return;
        }
        const alias = resolveThemeAlias(param);
        if (alias) {
            window.localStorage.setItem(PREVIEW_THEME_STORAGE_KEY, alias);
        }
    } catch {
        // Ignore quota / private-mode failures.
    }
}

/**
 * Full branding for the current (or provided) domain.
 * Safe to call from any component / util.
 */
export function getDomainBranding(domainKey?: string): DomainBranding {
    const hostKey = resolveKey(domainKey);
    const previewKey = domainKey ? undefined : getPreviewThemeOverride(hostKey);
    const key = previewKey || hostKey;
    const override = DOMAIN_BRANDING_MAP[key] || {};
    const logo = override.logo ?? DEFAULT_BRANDING.logo;

    const logoMinimal = override.logoMinimal ?? DEFAULT_BRANDING.logoMinimal;

    return {
        key,
        logo,
        logoHeader: override.logoHeader ?? logo,
        logoMinimal,
        favicon: override.favicon ?? logoMinimal,
        width: override.width ?? DEFAULT_BRANDING.width,
        compactWidth: override.compactWidth ?? DEFAULT_BRANDING.compactWidth,
        showTagline: override.showTagline ?? DEFAULT_BRANDING.showTagline,
        // Only fall back to default external URL when the domain uses default tagline branding
        externalUrl:
            'externalUrl' in override
                ? override.externalUrl
                : override.showTagline === false
                  ? undefined
                  : DEFAULT_BRANDING.externalUrl,
        palette: getDomainPalette(key),
    };
}

/**
 * Resolve a logo URL for a layout variant.
 * - `full` / `compact` → auth & loader logo
 * - `header`           → header desktop
 * - `minimal`          → header mobile / icon
 */
export function getDomainLogoSrc(variant: DomainLogoVariant = 'full', domainKey?: string): string {
    const branding = getDomainBranding(domainKey);
    switch (variant) {
        case 'minimal':
            return branding.logoMinimal;
        case 'header':
            return branding.logoHeader;
        case 'full':
        case 'compact':
        default:
            return branding.logo;
    }
}

/**
 * Legacy-friendly helper: logo details for full-width layouts (login style).
 * Prefer `getDomainBranding()` for new code.
 */
export function getDomainLogo(domainKey?: string): DomainBranding {
    return getDomainBranding(domainKey);
}

/** True when the domain uses default Aliro branding (tagline surfaces). */
export function isDefaultDomainBranding(domainKey?: string): boolean {
    return getDomainBranding(domainKey).showTagline === true;
}

/** Default marketing tagline for Aliro-branded domains. */
export function getDomainTagline(): string {
    return DEFAULT_TAGLINE;
}

/**
 * Open the brand external URL if configured (used on default Aliro logos).
 */
export function openDomainExternalUrl(domainKey?: string): void {
    const { externalUrl } = getDomainBranding(domainKey);
    if (externalUrl) {
        window.open(externalUrl, '_blank')?.focus();
    }
}

/** Apply domain favicon from branding (`favicon`, else `logoMinimal`). */
export function applyDomainFavicon(domainKey?: string): void {
    const branding = getDomainBranding(domainKey);
    const { favicon, key } = branding;
    const type = favicon.endsWith('.svg') ? 'image/svg+xml' : 'image/png';

    const tabTitles: Record<string, string> = {
        [DOMAIN.ALIROCANADA]: 'Aliro Canada',
        [DOMAIN.ALIROAUSTRALIA]: 'Aliro Australia',
        [DOMAIN.ALIROUK]: 'Aliro UK',
    };
    if (tabTitles[key]) {
        document.title = tabTitles[key];
    }

    // Replace the link node — updating href alone is unreliable in Chrome
    const existing = document.getElementById('favicon');
    existing?.remove();

    const link = document.createElement('link');
    link.id = 'favicon';
    link.rel = 'icon';
    link.type = type;
    link.href = favicon;
    document.head.appendChild(link);
}

/** Publish brand tokens on :root so shared form chrome can follow the tenant. */
export function applyDomainPaletteCssVars(domainKey?: string): void {
    if (typeof document === 'undefined') {
        return;
    }
    const vars = getDomainPaletteCssVars(getDomainBranding(domainKey).palette);
    const root = document.documentElement;
    (Object.entries(vars) as Array<[string, string]>).forEach(([name, value]) => {
        root.style.setProperty(name, value);
    });
}
